import { select, put, call, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import { setLoading, showSnackbar, toggleModal } from 'reducers/ui';
import { getSecurityId } from 'reducers/users';
import { errorHandler } from 'utils/errorMessages';
import API from 'services/api';
import {
  personnelPageActions,
  personnelFilterActions,
  personnelGridActions,
  personnelNS,
} from 'containers/Pages/People/Personnel';

import {
  getForceRefresh,
  getPersonnelListTransformed,
  getPersonnelFilterKey,
} from 'containers/Pages/People/Personnel/Selectors/personnelSelectors';
import isEmpty from 'lodash/isEmpty';

import config from 'config';
import { getGlobalUsers } from 'sagas/globalList';
const serviceUrl = config.api;

export function* fetchPersonnelListSaga({ payload }) {
  try {
    const { filters, from, pagination } = payload;
    const securityCompanyId = yield select(getSecurityId);
    const forceRefresh = yield select(getForceRefresh);
    const personnelList = yield select(getPersonnelListTransformed);
    const personnelFilterKey = yield select(getPersonnelFilterKey);
    // only make api call if forceRefersh is true  or  personnel list is empty or filter key is set
    if (
      !forceRefresh &&
      !isEmpty(personnelList) &&
      !personnelFilterKey &&
      !pagination // if pagination button is clicked, the allow to get new data
    ) {
      return;
    }

    const role = get(filters, 'role');
    const status = get(filters, 'status');
    const service = get(filters, 'service');
    const departmentId = get(filters, 'departmentId');

    const filtersOptions = {
      securityCompanyId,
      ...(filters?.site?.id && { siteId: filters.site.id }),
      roles: role ? (Array.isArray(role) ? role : [role]) : '',
      archived: get(filters, 'archived', false),
      service: service ? (Array.isArray(service) ? service : [service]) : '',
      status: status ? (Array.isArray(status) ? status : [status]) : '',
      search: get(filters, 'search'),
      from,
      departmentId,
    };
    const { data } = yield call(API.fetchUserList, filtersOptions);

    if (data.data !== null) {
      const totalEntries = get(data, 'meta.total');
      yield put(
        personnelGridActions.setGridList({ list: data.data, totalEntries }),
      );
    }
    yield put(personnelFilterActions.setFilterKey(false));
    yield put(personnelGridActions.setForceRefresh(false));
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchPersonnelTemplateCSV() {
  try {
    // the url is designed to automatically download the csv file
    window.open(`${serviceUrl}/users/users/import/csv/template`);
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchPersonnelDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    yield call(getGlobalUsers);

    const { data } = yield call(API.fetchUserDetail, id);
    // let shiftData = [];

    // if (shiftId) {
    //   shiftData = yield call(API.fetchShiftById, { id: shiftId });
    // }

    if (data.data) {
      yield put(personnelGridActions.setGridRowDetails({ details: data.data }));
    }
    // yield put(setPersonalDetailRaw(data.data));
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* createPersonnelSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { personnel } = payload;

    const { status } = yield call(API.addUser, personnel);
    if (status === 200) {
      // if the request fails, it will not toggle off the form and the user will not loose the work
      yield put(toggleModal(personnelNS));
    }

    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* updatePersonnelSaga({ payload }) {
  try {
    const { id, personnel } = payload;
    yield put(setLoading(true));
    const { status } = yield call(API.updateUser, id, personnel);

    if (status === 200) {
      yield call(fetchPersonnelDetailsSaga, { payload: { id } });
    }

    yield put(toggleModal(personnelNS));
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* assignToPatrolSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { personnel, patrolDef } = payload;
    yield call(API.assignPatrolToPersonnel, personnel, patrolDef);
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: 'Message sent',
      }),
    );
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* uploadPersonnelCSVSaga({ payload }) {
  try {
    const { personnelDoc } = payload;
    yield put(setLoading(true));
    yield call(API.uploadPersonnelCSV, personnelDoc);
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'success',
        message: 'Uploaded Personnel Document',
      }),
    );
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* personnelSagas() {
  yield takeLatest(personnelGridActions.fetchGridList, fetchPersonnelListSaga);
  yield takeLatest(personnelGridActions.createRowEntry, createPersonnelSaga);
  yield takeLatest(personnelGridActions.updateRowEntry, updatePersonnelSaga);
  yield takeLatest(
    personnelPageActions.fetchPersonnelCSVTemplate,
    fetchPersonnelTemplateCSV,
  );
  yield takeLatest(
    personnelGridActions.fetchGridRowDetails,
    fetchPersonnelDetailsSaga,
  );
  yield takeLatest(personnelPageActions.assignToPatrol, assignToPatrolSaga);
  yield takeLatest(
    personnelPageActions.uploadPersonnelCSV,
    uploadPersonnelCSVSaga,
  );
}
